import {
  selectIpawsHoldFlag,
  selectIpawsLastCapIdentifier,
} from 'features/Weather/features/WeatherAlerts/features/AlertCreator/state/selectors/alertCreatorSelectors';
import { setIpawsHoldFlag } from 'features/Weather/features/WeatherAlerts/features/AlertCreator/state/slices/alertCreatorSlice';
import { toast } from 'react-toastify';
import { femaApi } from 'services/ipaws/ipaws.api';

/**
 * `ipaws-message-update` — the backend tells the workplace that a FEMA message's
 * status changed (FE-03 / BE-08, decided 2026-08-26). Emitted by the Fusion edge
 * after it answers the submitter, scoped to the submitting workplace. The name
 * and payload are contract on both sides — never rename one without the other.
 */
export const IPAWS_MESSAGE_UPDATE_EVENT = 'ipaws-message-update';

/** The backend's vocabulary: the send gate's code plus the seven BE-07 response classes. */
export const IPAWS_MESSAGE_STATUSES = [
  'REACHED_SEND_POINT',
  'TRANSPORT_SUCCESS',
  'AUTHENTICATION_SUCCESS',
  'IPAWS_ACCEPTANCE',
  'IPAWS_ADVISORY',
  'IPAWS_WARNING',
  'IPAWS_REJECTION',
  'NETWORK_FAILURE',
] as const;

export type IpawsMessageStatus = (typeof IPAWS_MESSAGE_STATUSES)[number];

export type IpawsMessageUpdateEvent = {
  capIdentifier: string;
  status: IpawsMessageStatus;
  workplaceId: number;
  message?: string;
};

export const isIpawsMessageUpdateEvent = (value: unknown): value is IpawsMessageUpdateEvent => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.capIdentifier === 'string' &&
    candidate.capIdentifier.length > 0 &&
    typeof candidate.status === 'string' &&
    (IPAWS_MESSAGE_STATUSES as readonly string[]).includes(candidate.status) &&
    typeof candidate.workplaceId === 'number'
  );
};

const STATUS_TEXT: Record<IpawsMessageStatus, string> = {
  REACHED_SEND_POINT: 'reached the FEMA send gate: built, signed and verified, not sent',
  TRANSPORT_SUCCESS: 'reached IPAWS (transport succeeded)',
  AUTHENTICATION_SUCCESS: 'was authenticated by IPAWS',
  IPAWS_ACCEPTANCE: 'was accepted by IPAWS',
  IPAWS_ADVISORY: 'was accepted by IPAWS with an advisory',
  IPAWS_WARNING: 'was accepted by IPAWS with a warning',
  IPAWS_REJECTION: 'was rejected by IPAWS',
  NETWORK_FAILURE: 'could not reach IPAWS (network failure)',
};

const FAILURE_STATUSES: readonly IpawsMessageStatus[] = ['IPAWS_REJECTION', 'NETWORK_FAILURE'];

type StoreDispatch = (action: unknown) => unknown;
type Thunk = (dispatch: StoreDispatch, getState: () => unknown) => void;

/**
 * Apply one `ipaws-message-update` event:
 *  - release this screen's submission hold (the HTTP answer usually got there first);
 *  - toast only when the message is this screen's own last submission;
 *  - refresh the FEMA alert list for everyone in the workplace.
 * A payload that is not the contract is ignored — never a throw inside an SSE handler.
 */
export function applyIpawsMessageUpdate(dispatch: StoreDispatch, eventData: unknown): void {
  if (!isIpawsMessageUpdateEvent(eventData)) return;

  const thunk: Thunk = (innerDispatch, getState) => {
    const state = getState();

    if (selectIpawsHoldFlag(state)) innerDispatch(setIpawsHoldFlag(false));

    if (selectIpawsLastCapIdentifier(state) === eventData.capIdentifier) {
      const text = `FEMA alert ${eventData.capIdentifier} ${STATUS_TEXT[eventData.status]}.`;
      if (FAILURE_STATUSES.includes(eventData.status)) toast.error(text);
      else toast.info(text);
    }

    innerDispatch(femaApi.util.invalidateTags(['FemaAlertList', 'FemaAlert']));
  };

  dispatch(thunk);
}
